<template lang="pug">
    form(
        novalidate,
        :class="{ _inv: !valid }",
        @submit.prevent="submit")
        slot
</template>

<script>
    import object from './ib-form-object.vue';

    /**
     * A form that contains one or more controls and can be submitted. There are three ways to
     * submit an `ib-form`:
     *
     * - Programmatically, by calling `submit()` on a ref to the component.
     * - User, by clicking a `ib-button-submit` that is enclosed in the form or has `for` equal to the `id` of the form.
     * - User, by pressing "Enter" or a mobile equivalent while focused on a field.
     *
     * On submit, if a Vuex action is specified, that action will be dispatched. The options
     * passed with the action are either:
     *
     * - By default, the computed value of the form. This is an object with keys equal to control
     *   `name` properties, and values equal to the value input in the control. To achieve nested
     *   objects, insert an `ib-form-object`. To insert an entry without attaching it to a control,
     *   add an `ib-form-entry`. To achieve an array, insert an `ib-form-array`.
     * - If `options` is specified as an object, it will be passed directly.
     * - If `options` is specified as a function, the function will be called with the computed
     *   form value as its argument. The return value of the function will be passed.
     *
     * Regardless if a Vuex action is specified, a `submit` event will be emitted.
     */
    export default {
        name: 'ib-form',

        mixins: [
            object
        ],

        props: {
            /**
             * Vuex action to dispatch on submit. If not specified, a `submit` event will be
             * emitted but no other action will be taken.
             */
            action: String,
            /**
             * Options to be passed with dispatched action; may be specified as a `Function` that
             * generates the object if the object needs to be computed. If not specified, the
             * computed value of the form will be passed.
             */
            options: [Object,Function]
        },

        methods: {
            /**
             * Validate the form; if valid, mark as busy, dispatch a Vuex action if applicable,
             * and emit a `submit` event.
             */
            submit() {
                this.validate();
                if (this.valid) {
                    this.dispatching = true;
                    /**
                     * Emitted when a valid form is submitted.
                     * @event submit
                     * @type {object}
                     */
                    this.$emit('submit', this.ivalue);
                    if (this.action != null) {
                        let options;
                        switch (typeof this.options) {
                        case 'undefined':
                            options = this.ivalue;
                            break;
                        case 'function':
                            options = this.options(this.ivalue);
                            break;
                        default:
                            options = this.options;
                            break;
                        }
                        const result = this.$store.dispatch(this.action, options);
                        if (result?.finally) {
                            return result.then(response => {
                                /**
                                 * Emitted when the Vuex event was dispatched with a successful result.
                                 * @event success
                                 * @type {object}
                                 */
                                this.$emit('success', response);
                            }).catch(error => {
                                /**
                                 * Emitted when the Vuex action was dispatched with an error result.
                                 * @event error
                                 * @type {object}
                                 */
                                this.$emit('error', error);
                            }).finally(() => {
                                this.dispatching = false;
                            });
                        } else {
                            return result;
                        }
                    }
                } else {
                    /**
                     * Emitted when an invalid form cannot be submitted.
                     * @event submit
                     */
                    this.$emit('invalid');
                }
            },

            /**
             * Mark the form (and any submit button) as not busy.
             */
            done() {
                this.dispatching = false;
            }
        }
    }
</script>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        ib-form(ref="form", @submit="submit")
            ib-field(name="field", placeholder="field")
            ib-dropdown(name="dropdown", placeholder="dropdown", :data="['Adam','Pavlo','Maja','Steven']")
            ib-button-submit Submit
    </template>
    <script>
        {
            methods: {
                submit(data) {
                    setTimeout(() => {
                        this.$notify(JSON.stringify(data));
                        this.$refs.form.done();
                    }, 1000);
                }
            }
        }
    </script>
</ib-example>

<ib-example id="checkbox" name="Checkbox">
    <template lang="pug">
        ib-form(ref="form", @submit="submit")
            ib-checkbox(name="worthy", required) I acknowledge that I am worthy.
            ib-button-submit Submit
    </template>
    <script>
        {
            methods: {
                submit(data) {
                    setTimeout(() => {
                        this.$notify(JSON.stringify(data));
                        this.$refs.form.done();
                    }, 1000);
                }
            }
        }
    </script>
</ib-example>
